import { ReactElement } from 'react';

import { BlockRenderer } from '~/core/application/components';
import {
  renderFieldValue,
  transformFieldIntoBlock,
} from '~/core/shared/fields/FieldUtils';
import {
  AnyApplicationFormField,
  Field,
  FieldTypes,
} from '~/core/shared/types';
import { FieldElement } from '../application/form';

export type FormFieldRendererProps = {
  field: Field<FieldTypes>;
};

export const supportedBlockRenderTypes: FieldTypes[] = [
  'select',
  'typeahead',
  'phone',
  'currency',
  'number',
  'percent',
  'repeated_json',
  'time',
  'boolean',
  'date',
  'decimal',
  'email',
  'integer',
  'text',
  'html',
  'xml',
  'gender',
  'date_month_full_year',
  'category',
  'nested_select',
  'wysiwyg',
];

const renderAsLegacyField = (field: Field<FieldTypes>): ReactElement => {
  const contextAttr = { value: field.value }; // Or derive this from your state or props

  const fieldProps = {
    borderColor: 'gray.300',
    defaultValue: field.value || field.defaultValue,
    disabled: false,
    required: !!field.required,
    readOnly: field.readOnly,
    labelProps: {
      optionalText: '',
    },
    name: field.name,
    variant: 'outline',
    helpText: field.tooltip,
    size: 'sm',
  };

  return (
    <FieldElement
      contextAttr={contextAttr}
      field={field as unknown as AnyApplicationFormField}
      fieldProps={fieldProps}
    />
  );
};

export const FormFieldRenderer = ({
  field,
}: FormFieldRendererProps): ReactElement<FormFieldRendererProps> => {
  // @TODO: remove this once all field types are supported
  if (!supportedBlockRenderTypes.includes(field.type)) {
    return <>{renderAsLegacyField(field)}</>;
  }

  if (field.readOnly) {
    return <>{renderFieldValue(field)}</>;
  }

  return (
    <BlockRenderer
      block={{
        ...transformFieldIntoBlock(field),
        readOnly: field.readOnly,
        labelProps: { display: 'none' },
        helpText: field.tooltip,
        validationRules: {
          required: !!field.required,
        },
      }}
    />
  );
};
